import { ICard, cardToString } from '../card-types';

/**
 * Converts a string to TitleCase, e.g. 'john' -> 'John'
 */
function toTitleCase(name: string): string {
  if (!name || typeof name !== 'string') return '';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

// Common name suffixes that should be preserved with the last name
const NAME_SUFFIXES = [
  'jr',
  'jr.',
  'senior',
  'sr',
  'sr.',
  'i',
  'ii',
  'iii',
  'iv',
  'v',
  'vi',
  'vii',
  'viii',
  'ix',
  'x',
  'xi',
  'xii',
  'xiii',
  'xiv',
  'xv',
  '1st',
  '2nd',
  '3rd',
  '4th',
  '5th',
];

/**
 * Extracts the base last name and suffix from last name parts
 * @param lastNameParts - Array of last name parts
 * @returns Object with baseName and suffix
 */
function extractLastNameAndSuffix(lastNameParts: string[]): { baseName: string; suffix: string | null } {
  if (lastNameParts.length === 0) {
    return { baseName: '', suffix: null };
  }

  if (lastNameParts.length === 1) {
    // Special case: if the single part is a known suffix (like Roman numerals),
    // treat it as a suffix with empty baseName - this indicates the data is truncated
    const singlePart = lastNameParts[0].toLowerCase().replace(/\./g, '');
    if (NAME_SUFFIXES.includes(singlePart)) {
      return { baseName: '', suffix: lastNameParts[0] };
    }
    return { baseName: lastNameParts[0], suffix: null };
  }

  // Check if the last part is a suffix
  const lastPart = lastNameParts[lastNameParts.length - 1].toLowerCase().replace(/\./g, '');
  const isSuffix = NAME_SUFFIXES.includes(lastPart);

  if (isSuffix) {
    // If there's a suffix, take the last 2 parts (base name + suffix)
    const baseName = lastNameParts[lastNameParts.length - 2];
    const suffix = lastNameParts[lastNameParts.length - 1];
    return { baseName, suffix };
  } else {
    // If no suffix, take only the last part as the base name
    const baseName = lastNameParts[lastNameParts.length - 1];
    return { baseName, suffix: null };
  }
}

/**
 * Formats a suffix with proper capitalization
 * @param suffix - The suffix to format
 * @returns Properly formatted suffix
 */
function formatSuffix(suffix: string): string {
  const cleanSuffix = suffix.toLowerCase().replace(/\./g, '');

  // Handle roman numerals
  if (
    ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'xi', 'xii', 'xiii', 'xiv', 'xv'].includes(
      cleanSuffix,
    )
  ) {
    return cleanSuffix.toUpperCase();
  }

  // Handle ordinal numbers
  if (['1st', '2nd', '3rd', '4th', '5th'].includes(cleanSuffix)) {
    return cleanSuffix;
  }

  // Handle Jr./Sr. - add period if not present
  if (['jr', 'sr'].includes(cleanSuffix)) {
    return toTitleCase(cleanSuffix) + '.';
  }

  // Handle other suffixes
  if (['senior', 'junior'].includes(cleanSuffix)) {
    return toTitleCase(cleanSuffix);
  }

  // Default case - return with original formatting but title case
  return toTitleCase(suffix);
}

/**
 * Formats a full name to display only first and last name in proper case.
 * Removes middle names and ensures proper capitalization.
 * Properly handles suffixes like Jr., Sr., I, II, III, IV, etc.
 * Optionally applies length constraints for display:
 * - If > 16 characters: abbreviate to "F. Lastname" format
 * - If still > 16 characters: truncate lastname with "..."
 *
 * @param firstName - The first name (may include middle names)
 * @param lastName - The last name (may include multiple parts and suffixes)
 * @param options - Optional configuration object
 * @param options.truncate - Whether to apply 16-character length constraints (default: true)
 * @param options.returnType - What part of the name to return: 'full' | 'first' | 'last' (default: 'full')
 * @returns Formatted name with optional length constraints applied
 *
 * @example
 * formatRealName('john', 'doe') // 'John Doe'
 * formatRealName('JOHN', 'DOE') // 'John Doe'
 * formatRealName('john michael', 'doe') // 'John Doe'
 * formatRealName('john', 'williams jr') // 'John Williams Jr.'
 * formatRealName('john', 'smith iii') // 'John Smith III'
 * formatRealName('christopher', 'anderson') // 'C. Anderson' (with truncate: true)
 * formatRealName('christopher', 'anderson', { truncate: false }) // 'Christopher Anderson'
 * formatRealName('john', 'doe', { returnType: 'first' }) // 'John'
 * formatRealName('john', 'doe', { returnType: 'last' }) // 'Doe'
 * formatRealName('john', 'van der berg', { returnType: 'last' }) // 'Berg'
 * formatRealName('john', 'van der berg jr', { returnType: 'last' }) // 'Berg Jr.'
 */
export function formatRealName(
  firstName: string,
  lastName: string,
  options: {
    truncate?: boolean;
    returnType?: 'full' | 'first' | 'last';
  } = { truncate: true, returnType: 'full' },
): string {
  if (!firstName || !lastName) return '';

  // Clean and split first name to remove middle names
  const firstNameParts = firstName.trim().split(/\s+/);
  const lastNameParts = lastName.trim().split(/\s+/);

  // Take only the first part of firstName
  const cleanFirstName = firstNameParts[0];
  if (!cleanFirstName) return '';

  const formattedFirstName = toTitleCase(cleanFirstName);

  // Handle last name with potential suffixes
  const { baseName: cleanLastName, suffix } = extractLastNameAndSuffix(lastNameParts);
  if (!cleanLastName) return '';

  const formattedLastName = toTitleCase(cleanLastName);
  const formattedSuffix = suffix ? formatSuffix(suffix) : '';
  const fullLastName = formattedSuffix ? `${formattedLastName} ${formattedSuffix}` : formattedLastName;

  // Handle different return types
  if (options.returnType === 'first') {
    return formattedFirstName;
  }

  if (options.returnType === 'last') {
    return fullLastName;
  }

  // Full name without any constraints
  const fullName = `${formattedFirstName} ${fullLastName}`;

  // If truncation is disabled, return full name regardless of length
  if (!options.truncate) {
    return fullName;
  }

  // Apply length constraints (original behavior)
  if (fullName.length <= 16) {
    return fullName;
  }

  // If too long, try abbreviated first name
  const abbreviatedName = `${formattedFirstName.charAt(0)}. ${fullLastName}`;
  if (abbreviatedName.length <= 16) {
    return abbreviatedName;
  }

  // If still too long, truncate last name (but preserve suffix if possible)
  const maxLastNameLength = 16 - 3 - (formattedSuffix ? formattedSuffix.length + 1 : 0) - 3; // Account for "F. ", suffix, and "..."
  if (maxLastNameLength > 0) {
    const truncatedBaseName = formattedLastName.substring(0, maxLastNameLength);
    const truncatedFullLastName = formattedSuffix
      ? `${truncatedBaseName}... ${formattedSuffix}`
      : `${truncatedBaseName}...`;
    return `${formattedFirstName.charAt(0)}. ${truncatedFullLastName}`;
  }

  // If we can't fit the suffix, fall back to original behavior
  const maxFallbackLength = 16 - 3 - 3; // 16 total - "F. " (3 chars) - "..." (3 chars)
  const truncatedLastName = fullLastName.substring(0, maxFallbackLength);
  return `${formattedFirstName.charAt(0)}. ${truncatedLastName}...`;
}

/**
 * Formats an alias string to ensure proper capitalization.
 * If it's a single word (alias), applies title case.
 * If it's multiple words (real name), uses the existing formatRealName function.
 *
 * @param alias - The alias string to format
 * @returns Formatted alias with proper capitalization
 *
 * @example
 * formatAlias('alice') // 'Alice' (single word alias)
 * formatAlias('travis mathis iv') // 'Travis Mathis IV' (real name with suffix)
 * formatAlias('john smith iii') // 'John Smith III' (real name with suffix)
 * formatAlias('bob jr') // 'Bob Jr.' (real name with suffix)
 */
export function formatAlias(alias: string): string {
  if (!alias || typeof alias !== 'string') return '';

  const parts = alias.trim().split(/\s+/);
  if (parts.length === 0) return '';

  // Single word = alias, return as is to respect the original player's formatting
  if (parts.length === 1) {
    return parts[0];
  }

  // Multiple words = real name, use formatRealName with truncate: true
  const firstName = parts[0];

  // Use extractLastNameAndSuffix to properly separate last name and suffix
  const lastNameParts = parts.slice(1);
  const { baseName: lastName, suffix } = extractLastNameAndSuffix(lastNameParts);

  // Handle case where data appears to be truncated (empty baseName with suffix)
  if (!lastName && suffix) {
    // Data is truncated - we only have firstName + suffix, missing the actual lastName
    // Format the suffix properly and return firstName + formatted suffix
    const formattedSuffix = formatSuffix(suffix);
    return `${toTitleCase(firstName)} ${formattedSuffix}`;
  }

  // Reconstruct the lastName with suffix if it exists
  const fullLastName = suffix ? `${lastName} ${suffix}` : lastName;

  return formatRealName(firstName, fullLastName, { truncate: true });
}

/**
 * Depending on whether the table is high stakes or not, returns either the player's alias
 * or their full name ("FirstName LastName").
 */
export function getPlayerDisplayName(
  seat: { user: { firstName: string; lastName: string; alias: string } },
  isHighStakes: boolean = false,
): string {
  if (isHighStakes) {
    return formatRealName(seat.user.firstName, seat.user.lastName, { truncate: true });
  }
  return seat.user.alias;
}

export function formatCards(cards: ICard[]): string {
  return cards ? cards.map((card) => (card ? cardToString(card) : '??')).join(', ') : '';
}
